import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
// A coarse copy of the same projected outlines the interactive map uses (11KB, not
// 90KB). It is imported eagerly on purpose: the home page's map is server-rendered
// static SVG, so it paints with the first byte and needs no JavaScript at all.
import { US_PREVIEW, US_PREVIEW_VIEWBOX } from "@/lib/geo/us-preview";
import { STATES } from "@/lib/states";
import { RISK_LEVELS, riskLabel, riskVar, NO_DATA, type RiskLevel } from "@/lib/risk";
import { inPeakCount } from "@/lib/season";
import { TonePattern } from "@/components/map/tone-pattern";

const RISK_BY_ABBR = new Map(STATES.map((s) => [s.abbr, s.risk]));
const SLUG_BY_ABBR = new Map(STATES.map((s) => [s.abbr, s.slug]));
const NAME_BY_ABBR = new Map(STATES.map((s) => [s.abbr, s.name]));

/** Same fills as the full map: flat colour with the quiet texture from TonePattern. */
function fillFor(abbr: string): string {
  const level = RISK_BY_ABBR.get(abbr);
  return `url(#home-fill-${level ?? "none"})`;
}

const FOREGROUND: Record<RiskLevel, string> = {
  low: "var(--risk-low-foreground)",
  emerging: "var(--risk-emerging-foreground)",
  established: "var(--risk-established-foreground)",
  high: "var(--risk-high-foreground)",
};

function HomeFills({ prefix, size, line }: { prefix: string; size: number; line: number }) {
  return (
    <defs>
      {RISK_LEVELS.map((level, i) => (
        <TonePattern
          key={level}
          id={`${prefix}-${level}`}
          tone={i as 0 | 1 | 2 | 3}
          fill={riskVar[level]}
          ink={FOREGROUND[level]}
          size={size}
          line={line}
        />
      ))}
      <TonePattern
        id={`${prefix}-none`}
        tone="none"
        fill={NO_DATA.var}
        ink="var(--risk-none-foreground)"
        size={size}
        line={line}
      />
    </defs>
  );
}

/** How many states sit at each level, for the figure's text alternative. */
function levelCounts() {
  return RISK_LEVELS.map((level) => ({
    level,
    count: STATES.filter((s) => s.risk === level).length,
  }));
}

const MONTH = new Intl.DateTimeFormat("en-US", { month: "long" });

/**
 * "October: peak season is under way in 6 of the 35 states…", from each state's
 * published peak-season window and the reader's own clock. Rendered after mount so a
 * cached page never shows last month's line.
 */
function SeasonLine() {
  const [line, setLine] = useState<string | null>(null);
  useEffect(() => {
    const now = new Date();
    const present = STATES.filter((s) => s.risk !== "low");
    const active = inPeakCount(
      present.map((s) => s.peakSeason),
      now,
    );
    const month = MONTH.format(now);
    setLine(
      active === 0
        ? `${month}: outside peak season in every state where Lone Star ticks are found. They can still bite on warm days.`
        : `${month}: peak season is under way in ${active} of the ${present.length} states where Lone Star ticks are found or spreading.`,
    );
  }, []);
  return (
    <p className="min-h-[1.5rem] text-sm text-foreground" aria-live="polite">
      {line && (
        <>
          <span
            aria-hidden
            className="mr-2 inline-block h-2 w-2 rounded-full bg-rust align-middle"
          />
          {line}
        </>
      )}
    </p>
  );
}

/**
 * The home page's map, set as a figure in a field guide: the plate, a key, a numbered
 * caption. Every state is a link to its page, so the picture is also the index. When the
 * search beside it answers, the answered state is drawn forward and the rest recede.
 */
export function HomeMapFigure({ highlight = null }: { highlight?: string | null }) {
  const counts = levelCounts();
  const reported = counts.filter((c) => c.level !== "low").reduce((sum, c) => sum + c.count, 0);
  // Paint the highlighted state last so its outline sits on top of its neighbours'.
  const order = highlight
    ? [
        ...US_PREVIEW.filter((g) => g.abbr !== highlight),
        ...US_PREVIEW.filter((g) => g.abbr === highlight),
      ]
    : US_PREVIEW;

  return (
    <figure className="w-full">
      <SeasonLine />
      <svg
        viewBox={US_PREVIEW_VIEWBOX}
        role="group"
        aria-label={`Map of the United States shaded by where Lone Star ticks are reported: ${counts
          .map((c) => `${c.count} ${riskLabel[c.level].toLowerCase()}`)
          .join(", ")}. Each state links to its page.`}
        className="mt-3 h-auto w-full"
      >
        <HomeFills prefix="home-fill" size={11} line={1.25} />
        {order.map((g) => {
          const slug = SLUG_BY_ABBR.get(g.abbr);
          const level = RISK_BY_ABBR.get(g.abbr);
          const on = highlight === g.abbr;
          const path = (
            <path
              d={g.d}
              fill={fillFor(g.abbr)}
              stroke={on ? "var(--foreground)" : "var(--background)"}
              strokeWidth={on ? 2.5 : 1}
              strokeLinejoin="round"
              className="map-fill transition-[opacity,stroke] duration-300 hover:opacity-80"
              style={{
                opacity: highlight && !on ? 0.35 : undefined,
                animationDelay: `${(level ? RISK_LEVELS.indexOf(level) : 0) * 110}ms`,
              }}
            />
          );
          return slug ? (
            <a key={g.abbr} href={`/states/${slug}`} aria-label={NAME_BY_ABBR.get(g.abbr)}>
              {path}
            </a>
          ) : (
            <g key={g.abbr}>{path}</g>
          );
        })}
      </svg>
      <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-foreground">
        {RISK_LEVELS.map((level) => (
          <li key={level} className="flex items-center gap-1.5">
            <svg aria-hidden width="14" height="14" className="shrink-0 ring-1 ring-foreground/25">
              <defs>
                <TonePattern
                  id={`home-key-${level}`}
                  tone={RISK_LEVELS.indexOf(level) as 0 | 1 | 2 | 3}
                  fill={riskVar[level]}
                  ink={FOREGROUND[level]}
                  size={5}
                  line={0.8}
                />
              </defs>
              <rect width="14" height="14" fill={`url(#home-key-${level})`} />
            </svg>
            {riskLabel[level]}
          </li>
        ))}
      </ul>
      <figcaption className="mt-3 border-t border-border pt-3 text-sm leading-relaxed text-muted-foreground">
        <span className="font-semibold text-foreground">Fig. 1.</span>{" "}
        {highlight && NAME_BY_ABBR.get(highlight) ? (
          <>
            <span className="text-foreground">{NAME_BY_ABBR.get(highlight)}</span>, outlined. Lone
            Star ticks are found or spreading in {reported} of the 50 states and D.C.{" "}
          </>
        ) : (
          <>
            Lone Star ticks are found or spreading in {reported} of the 50 states and D.C., mostly
            across the South, East and Midwest.{" "}
          </>
        )}
        <Link to="/map" className="link font-semibold">
          Open the full map
        </Link>{" "}
        to zoom to your county.
      </figcaption>
    </figure>
  );
}
